import { PressurePoint, CopPoint, PressureSensorArray } from '../types/ski-data';

/**
 * Calculate Center of Pressure (COP) from a pressure sensor array.
 * COP is the weighted average of all pressure point coordinates,
 * weighted by their pressure values.
 */
export function calculateCop(points: PressurePoint[]): CopPoint | null {
  if (points.length === 0) return null;
  
  let totalPressure = 0;
  let weightedX = 0;
  let weightedY = 0;
  
  for (const p of points) {
    totalPressure += p.value;
    weightedX += p.x * p.value;
    weightedY += p.y * p.value;
  }
  
  if (totalPressure === 0) return null;
  
  return {
    x: weightedX / totalPressure,
    y: weightedY / totalPressure,
    timestamp_ms: 0, // caller should set this
  };
}

/**
 * Calculate COP for both feet.
 */
export function calculateDualCop(
  leftArray: PressureSensorArray,
  rightArray: PressureSensorArray,
  timestampMs: number
): { left: CopPoint | null; right: CopPoint | null } {
  const left = calculateCop(leftArray.points);
  const right = calculateCop(rightArray.points);
  
  return {
    left: left ? { ...left, timestamp_ms: timestampMs } : null,
    right: right ? { ...right, timestamp_ms: timestampMs } : null,
  };
}

/**
 * Calculate COP trace for a series of pressure arrays.
 */
export function calculateCopTrace(
  pressureArrays: { left: PressureSensorArray; right: PressureSensorArray; timestamp_ms: number }[],
  foot: 'left' | 'right'
): CopPoint[] {
  const trace: CopPoint[] = [];
  for (const arr of pressureArrays) {
    const cop = calculateCop(arr[foot].points);
    if (cop) {
      trace.push({ ...cop, timestamp_ms: arr.timestamp_ms });
    }
  }
  return trace;
}

/**
 * Calculate combined COP (treating both feet as a single system).
 * Returns COP in a coordinate system where left foot is at y = -50 and right foot at y = +50.
 */
export function calculateCombinedCop(
  leftArray: PressureSensorArray,
  rightArray: PressureSensorArray,
  timestampMs: number
): CopPoint | null {
  const allPoints: PressurePoint[] = [
    ...leftArray.points.map(p => ({ ...p, y: p.y - 50 })),
    ...rightArray.points.map(p => ({ ...p, y: p.y + 50 })),
  ];
  const cop = calculateCop(allPoints);
  return cop ? { ...cop, timestamp_ms: timestampMs } : null;
}
